"use client";

import Image from "next/image";
import { IconBrandAppleFilled } from "@/lib/icons";
import Container from "../container";
import styles from "./mblbnner.module.css";
import CMSLink from "../common/CMSLink";
import CMSTextBlock from "../common/CMSTextBlock";
import GOOGLE_PLAY from "@/public/google-play.svg";
import { useStorePlatform } from "@/lib/useStorePlatform";
import placeholder from "@/components/Event/assests/placeholder.png";
import { useState } from "react";

const STORE_BADGES = {
  "google-play-fill": {
    eyebrow: "GET IT ON",
    title: "Google Play",
    icon: <Image src={GOOGLE_PLAY} alt="Google Play" width={24} height={24} />,
    iconLight: (
      <Image src={GOOGLE_PLAY} alt="Google Play" width={24} height={24} />
    ),
  },
  "apple-fill": {
    eyebrow: "Download on the",
    title: "App Store",
    icon: <IconBrandAppleFilled size={24} color="#fff" />,
    iconLight: <IconBrandAppleFilled size={24} color="#021526" />,
  },
};

function getStoreMeta(iconKey, fallbackKey) {
  if (iconKey && STORE_BADGES[iconKey]) return STORE_BADGES[iconKey];
  const lower = (iconKey || "").toLowerCase();
  if (
    lower.includes("apple") ||
    lower.includes("app-store") ||
    lower.includes("ios")
  ) {
    return STORE_BADGES["apple-fill"];
  }
  if (lower.includes("google") || lower.includes("play")) {
    return STORE_BADGES["google-play-fill"];
  }
  return STORE_BADGES[fallbackKey] || STORE_BADGES["google-play-fill"];
}

export function StoreBadge({
  btn,
  iconKey,
  variant = "dark",
  fallbackIconKey = "google-play-fill",
}) {
  const meta = getStoreMeta(iconKey, fallbackIconKey);
  if (!meta) return null;

  const isLight = variant === "light";

  return (
    <CMSLink
      config={btn}
      useIn="other"
      className={`${styles.storeBadge} ${isLight ? styles.storeBadgeLight : ""}`}
    >
      <span className={styles.storeBadgeIcon}>
        {isLight ? meta.iconLight : meta.icon}
      </span>
      <span className={styles.storeBadgeText}>
        <span
          className={`${styles.storeBadgeEyebrow} ${isLight ? styles.storeBadgeEyebrowLight : ""}`}
        >
          {meta.eyebrow}
        </span>
        <span
          className={`${styles.storeBadgeTitle} ${isLight ? styles.storeBadgeTitleLight : ""}`}
        >
          {meta.title}
        </span>
      </span>
    </CMSLink>
  );
}

// Resolve CMS color value (string hex or metaColor object) → CSS string
function resolveColor(colorValue) {
  if (!colorValue) return null;
  if (typeof colorValue === "string") return colorValue;
  if (colorValue?.metaColor?.isValid) {
    const { r, g, b, a = 1 } = colorValue.metaColor;
    return `rgba(${r}, ${g}, ${b}, ${a})`;
  }
  return null;
}

function normalizeButtons(raw, defaultUrl) {
  // If the CMS field is completely missing (undefined/null), fall back to defaultUrl (if provided)
  if (raw == null) {
    return defaultUrl ? [{ type: "external", url: defaultUrl }] : [];
  }

  // If it's a string, use it. If empty string, don't show the button.
  if (typeof raw === "string") {
    const trimmed = raw.trim();
    return trimmed ? [{ type: "external", url: trimmed }] : [];
  }

  // If it's an array, only map over valid items. If array is empty, don't show the button.
  if (Array.isArray(raw)) {
    if (raw.length === 0) return [];

    return raw
      .map((item) => {
        if (typeof item === "string") {
          return { type: "external", url: item.trim() };
        }
        const url =
          item?.url?.url ||
          item?.url ||
          item?.link ||
          item?.href ||
          item?.play_store ||
          item?.app_store ||
          "";
        return {
          ...item,
          type: item?.type || item?.url?.type || "external",
          url,
        };
      })
      .filter((item) => Boolean(item.url));
  }

  // If it's an object, extract URL. If missing, don't show the button.
  if (typeof raw === "object") {
    const url =
      raw?.url?.url ||
      raw?.url ||
      raw?.link ||
      raw?.href ||
      raw?.play_store ||
      raw?.app_store ||
      "";
    if (!url) return [];
    return [
      {
        ...raw,
        type: raw?.type || raw?.url?.type || "external",
        url,
      },
    ];
  }

  return [];
}

export default function MobileBanner({ data }) {
  if (!data) return null;

  const headingLines = data?.headingLines || null;
  const subheadingLines = data?.subheadingLines || null;
  const heading = data?.heading || null;
  const subheading = data?.subheading || null;
  const desktopImageUrl = data?.desktop_image?.[0]?.url;
  const mobileImageUrl = data?.mobile_image?.[0]?.url;

  const hasText =
    (Array.isArray(headingLines) && headingLines.length > 0) ||
    (Array.isArray(subheadingLines) && subheadingLines.length > 0) ||
    Boolean(heading) ||
    Boolean(subheading);
  const hasOriginalImage = Boolean(desktopImageUrl || mobileImageUrl);

  if (!hasText && !hasOriginalImage) return null;
  const hasImage = true;

  const bgColor = resolveColor(data?.backgroundColor) || "#FADF93";

  const defaultPlayStoreUrl =
    process.env.NEXT_PUBLIC_ANDROID_STORE_LINK ||
    process.env.NEXT_PUBLIC_MOBILE_APP ||
    "https://play.google.com/store/apps/details?id=com.tykkit.customer";

  const defaultAppStoreUrl =
    process.env.NEXT_PUBLIC_IOS_APP || "https://apps.apple.com";

  const playStoreButtons = data?.play_store || data?.play_store_link || null;
  const appStoreButtons = data?.app_store || data?.app_store_link || null;
  const playStoreIcon = data?.play_store_icon || "google-play-fill";
  const appStoreIcon = data?.app_store_icon || "apple-fill";

  const [imgErrorDesktop, setImgErrorDesktop] = useState(false);
  const [imgErrorMobile, setImgErrorMobile] = useState(false);

  const platform = useStorePlatform();
  const showAndroid = platform === "android" || platform === "both";
  const showApple = platform === "ios" || platform === "both";

  const playStoreList = normalizeButtons(playStoreButtons, defaultPlayStoreUrl);
  const appStoreList = normalizeButtons(appStoreButtons, defaultAppStoreUrl);

  return (
    <section className={styles.section} id="mobile-app-banner">
      <div className={styles.panel} style={{ background: bgColor }}>
        <Container>
          <div className={styles.layout}>
            {/* ── Content column ── */}
            <div className={styles.contentCol}>
              <div className={styles.contentWrapper}>
                <CMSTextBlock
                  headingLines={headingLines}
                  subheadingLines={subheadingLines}
                  heading={heading}
                  subheading={subheading}
                  titleTag="p"
                  headingClassName={styles.title}
                  subheadingClassName={styles.desc}
                />
              </div>

              <div className={styles.actions}>
                {showAndroid &&
                  playStoreList.map((btn, index) => (
                    <StoreBadge
                      key={`play-${index}`}
                      btn={btn}
                      iconKey={playStoreIcon}
                      fallbackIconKey="google-play-fill"
                    />
                  ))}
                {showApple &&
                  appStoreList.map((btn, index) => (
                    <StoreBadge
                      key={`app-${index}`}
                      btn={btn}
                      iconKey={appStoreIcon}
                      fallbackIconKey="apple-fill"
                    />
                  ))}
              </div>
            </div>

            {/* ── Image column ── */}
            {hasImage && (
              <>
                <div className={styles.imageColDesktop}>
                  <Image
                    src={imgErrorDesktop || !desktopImageUrl && !mobileImageUrl ? placeholder : (desktopImageUrl || mobileImageUrl)}
                    onError={() => setImgErrorDesktop(true)}
                    alt="Download the app on Desktop"
                    width={1533}
                    height={861}
                    sizes="(max-width: 992px) 0vw, 45vw"
                    className={styles.bannerImg}
                  />
                </div>
                <div className={styles.imageColMobile}>
                  <Image
                    src={imgErrorMobile || !mobileImageUrl && !desktopImageUrl ? placeholder : (mobileImageUrl || desktopImageUrl)}
                    onError={() => setImgErrorMobile(true)}
                    alt="Download the app on Mobile"
                    width={1533}
                    height={861}
                    sizes="(max-width: 992px) 90vw, 0vw"
                    className={styles.bannerImgMobile}
                  />
                </div>
              </>
            )}
          </div>
        </Container>
      </div>
    </section>
  );
}
